import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, ShieldCheck, Send } from 'lucide-react';
import { companyInfo } from '../data';
import useDocumentTitle from '../hooks/useDocumentTitle';

export default function Contact() {
  useDocumentTitle('Kontakt');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    message: '',
    website_url: '' // Honeypot field
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormStatus({
          type: 'success',
          message: 'Tak for din besked! Jeg har modtaget din henvendelse og vender tilbage til dig hurtigst muligt.',
        });
        setFormData({ name: '', phone: '', email: '', address: '', message: '', website_url: '' });
      } else {
        throw new Error('Fejl ved indsendelse');
      }
    } catch (error) {
      setFormStatus({
        type: 'error',
        message: 'Der skete en fejl under afsendelsen. Prøv venligst igen eller ring til mig direkte på ' + companyInfo.phone,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full bg-neutral-50 min-h-screen">
      {/* Page Header */}
      <section className="bg-neutral-950 text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img
            src="https://assets.auxodev.dk/assets/58aac8da-8467-43e1-8dec-94cd5cd444a3/237c7784-9aff-46d8-a0b4-08ea733e25a2.jpg"
            alt="Murerarbejde"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <div className="w-16 h-1 bg-red-700 mb-4" />
          <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight">Kontakt mig</h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column: Contact Details */}
          <div className="lg:col-span-5 space-y-8">
            <div className="bg-white border border-neutral-200 p-8 rounded-sm shadow-sm space-y-6">
              <h2 className="text-2xl font-extrabold text-neutral-950 tracking-tight">
                Direkte Kontaktoplysninger
              </h2>
              <p className="text-neutral-600 text-sm leading-relaxed">
                Du er altid velkommen til at ringe eller skrive til mig. Jeg svarer hurtigt på alle henvendelser og kommer gerne ud til en uforpligtende besigtigelse af din opgave i Langå og omegn.
              </p>

              <div className="space-y-4 pt-4 border-t border-neutral-100">
                <div className="flex items-start gap-4">
                  <div className="text-neutral-950 flex-shrink-0 pt-1">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wider font-bold">Ring til mig</p>
                    <a href={`tel:${companyInfo.phone.replace(/\s+/g, '')}`} className="text-lg font-bold text-neutral-950 hover:text-red-700 transition-colors">
                      {companyInfo.phone}
                    </a>
                    <p className="text-xs text-neutral-400">Hurtigt svar på telefonen</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="text-neutral-950 flex-shrink-0 pt-1">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wider font-bold">Send en e-mail</p>
                    <a href={`mailto:${companyInfo.email}`} className="text-lg font-bold text-neutral-950 hover:text-red-700 transition-colors">
                      {companyInfo.email}
                    </a>
                    <p className="text-xs text-neutral-400">Vi svarer inden for 24 timer</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="text-neutral-950 flex-shrink-0 pt-1">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="text-xs text-neutral-500 uppercase tracking-wider font-bold">Adresse</p>
                    <p className="text-base font-bold text-neutral-950">{companyInfo.address}</p>
                    <p className="text-xs text-neutral-400">Lokalt murerfirma i Langå</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Opening Hours & CVR */}
            <div className="bg-white border border-neutral-200 p-8 rounded-sm shadow-sm space-y-6">
              <div className="flex items-center gap-2 text-neutral-950">
                <Clock size={20} className="text-neutral-950" />
                <h3 className="font-bold text-lg">Åbningstider</h3>
              </div>
              <ul className="space-y-3 text-sm">
                {companyInfo.openingHours.map((item, idx) => (
                  <li key={idx} className="flex justify-between items-center border-b border-neutral-100 pb-2 last:border-0 last:pb-0">
                    <span className="text-neutral-600">{item.days}</span>
                    <span className="text-neutral-950 font-bold text-xs">{item.hours}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-4 border-t border-neutral-100 text-xs text-neutral-500 flex justify-between">
                <span>CVR-nummer:</span>
                <span className="font-bold text-neutral-800">{companyInfo.cvr}</span>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7 bg-white border border-neutral-200 p-8 md:p-10 rounded-sm shadow-sm">
            <h2 className="text-2xl font-extrabold text-neutral-950 tracking-tight mb-2">
              Send en besked
            </h2>
            <p className="text-neutral-600 text-sm mb-8">
              Udfyld formularen nedenfor med dine kontaktoplysninger og en kort beskrivelse af din opgave, så vender jeg hurtigt tilbage med et uforpligtende tilbud.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Honeypot field - absolutely hidden from humans */}
              <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                <label htmlFor="website_url">Do not fill this out</label>
                <input
                  type="text"
                  id="website_url"
                  name="website_url"
                  value={formData.website_url}
                  onChange={handleInputChange}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2">
                    Navn *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-neutral-300 rounded-sm px-4 py-3 text-sm text-neutral-900 focus:outline-none focus:border-red-700"
                    placeholder="Dit fulde navn"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2">
                    Telefonnummer *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-neutral-300 rounded-sm px-4 py-3 text-sm text-neutral-900 focus:outline-none focus:border-red-700"
                    placeholder="F.eks. 12 34 56 78"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2">
                    E-mail *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-neutral-300 rounded-sm px-4 py-3 text-sm text-neutral-900 focus:outline-none focus:border-red-700"
                    placeholder="din@email.dk"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2">
                    Adresse (hvor opgaven skal udføres)
                  </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full bg-white border border-neutral-300 rounded-sm px-4 py-3 text-sm text-neutral-900 focus:outline-none focus:border-red-700"
                    placeholder="F.eks. Gadenavn 12, 8870 Langå"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-700 mb-2">
                  Beskrivelse af mureropgaven *
                </label>
                <textarea
                  name="message"
                  required
                  rows="6"
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full bg-white border border-neutral-300 rounded-sm px-4 py-3 text-sm text-neutral-900 focus:outline-none focus:border-red-700 resize-none"
                  placeholder="Beskriv opgaven så detaljeret som muligt (f.eks. omfang, materialer, tidsramme)..."
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-red-700 hover:bg-red-800 disabled:bg-neutral-400 text-white text-xs font-bold uppercase tracking-wider py-4 rounded-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg shadow-md flex items-center justify-center gap-2 cursor-pointer"
              >
                <Send size={14} />
                <span>{submitting ? 'Sender...' : 'Send besked & få tilbud'}</span>
              </button>

              {formStatus.message && (
                <div
                  className={`p-4 rounded-sm text-sm font-medium ${
                    formStatus.type === 'success'
                      ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}
                >
                  {formStatus.message}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
